<script setup>
import { computed, onMounted, ref } from 'vue';
import AppointmentRequestsAPI from 'dashboard/api/appointmentRequests';
import Button from 'dashboard/components-next/button/Button.vue';

const requests = ref([]);
const loading = ref(true);
const error = ref('');
const filter = ref('all');
const statuses = ['pending', 'confirmed', 'rescheduled', 'completed', 'cancelled'];
const savingId = ref(null);

const filtered = computed(() => filter.value === 'all'
  ? requests.value
  : requests.value.filter(item => item.status === filter.value));

const load = async () => {
  loading.value = true;
  try {
    requests.value = (await AppointmentRequestsAPI.get()).data;
    error.value = '';
  } catch {
    error.value = 'Appointment requests could not be loaded.';
  } finally {
    loading.value = false;
  }
};

const updateStatus = async (item, status) => {
  const previous = item.status;
  item.status = status;
  try {
    await AppointmentRequestsAPI.update(item.id, { status });
  } catch {
    item.status = previous;
    error.value = 'The appointment status could not be updated.';
  }
};

const saveDetails = async item => {
  savingId.value = item.id;
  try {
    const { data } = await AppointmentRequestsAPI.update(item.id, {
      confirmed_date: item.confirmed_date || null,
      confirmed_time: item.confirmed_time || null,
      internal_notes: item.internal_notes || null,
      meeting_url: item.meeting_url || null,
    });
    Object.assign(item, data);
    error.value = '';
  } catch {
    error.value = 'The appointment details could not be saved.';
  } finally {
    savingId.value = null;
  }
};

const whatsappUrl = item => {
  const phone = String(item.phone || '').replace(/[^\d]/g, '');
  const slot = item.confirmed_date && item.confirmed_time
    ? `${item.confirmed_date} at ${item.confirmed_time}`
    : 'the requested slot';
  const message = `Hello ${item.name}, your Agamagizh ${item.consultation_mode} consultation is confirmed for ${slot}.${item.meeting_url ? ` Join securely here: ${item.meeting_url}` : ''}`;
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
};

const formatDate = value => new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' }).format(new Date(`${value}T00:00:00`));

onMounted(load);
</script>

<template>
  <div class="flex h-full flex-col overflow-auto bg-n-background-1 p-6 lg:p-8">
    <header class="mb-6 flex flex-wrap items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-semibold text-n-slate-12">Appointments</h1>
        <p class="mt-1 text-sm text-n-slate-11">Consultation requests submitted through the Agamagizh website</p>
      </div>
      <Button label="Refresh" icon="i-lucide-refresh-cw" slate sm :is-loading="loading" @click="load" />
    </header>

    <div class="mb-5 flex flex-wrap gap-2">
      <button v-for="status in ['all', ...statuses]" :key="status" type="button"
        class="rounded-full border px-3 py-1.5 text-sm capitalize"
        :class="filter === status ? 'border-n-brand bg-n-brand text-white' : 'border-n-weak text-n-slate-11'"
        @click="filter = status">{{ status }}</button>
    </div>

    <p v-if="error" class="mb-5 rounded-xl border border-n-ruby-5 bg-n-ruby-2 p-4 text-sm text-n-ruby-11">{{ error }}</p>
    <p v-if="!loading && !filtered.length" class="rounded-2xl border border-n-weak bg-n-alpha-3 p-8 text-center text-n-slate-11">No appointment requests in this view.</p>

    <section class="grid gap-4 xl:grid-cols-2">
      <article v-for="item in filtered" :key="item.id" class="rounded-2xl border border-n-weak bg-n-alpha-3 p-5 shadow-sm">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2 class="font-semibold text-n-slate-12">{{ item.name }} <span v-if="item.age" class="font-normal text-n-slate-10">· {{ item.age }} years</span></h2>
            <a class="mt-1 inline-block text-sm text-n-brand" :href="`tel:${item.phone}`">{{ item.phone }}</a>
          </div>
          <select class="rounded-lg border border-n-weak bg-n-alpha-3 px-3 py-2 text-sm capitalize text-n-slate-12" :value="item.status" @change="updateStatus(item, $event.target.value)">
            <option v-for="status in statuses" :key="status" :value="status">{{ status }}</option>
          </select>
        </div>
        <div class="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div><p class="text-n-slate-10">Consultation</p><p class="mt-1 capitalize text-n-slate-12">{{ item.consultation_mode }}</p></div>
          <div><p class="text-n-slate-10">Preferred slot</p><p class="mt-1 text-n-slate-12">{{ formatDate(item.preferred_date) }} · {{ item.preferred_time }}</p></div>
        </div>
        <div class="mt-4 rounded-xl bg-n-alpha-2 p-4 text-sm">
          <p class="mb-1 text-xs font-medium uppercase tracking-wide text-n-slate-10">Health concern</p>
          <p class="whitespace-pre-wrap text-n-slate-12">{{ item.concern }}</p>
        </div>
        <div class="mt-4 grid gap-3 sm:grid-cols-2">
          <label class="text-xs text-n-slate-10">Confirmed date
            <input v-model="item.confirmed_date" type="date" class="mt-1 w-full rounded-lg border border-n-weak bg-n-alpha-2 p-2 text-sm text-n-slate-12" />
          </label>
          <label class="text-xs text-n-slate-10">Confirmed time
            <input v-model="item.confirmed_time" type="text" placeholder="10:00 AM" class="mt-1 w-full rounded-lg border border-n-weak bg-n-alpha-2 p-2 text-sm text-n-slate-12" />
          </label>
          <label v-if="item.consultation_mode === 'online'" class="text-xs text-n-slate-10 sm:col-span-2">Video consultation link
            <input v-model="item.meeting_url" type="url" placeholder="https://…" class="mt-1 w-full rounded-lg border border-n-weak bg-n-alpha-2 p-2 text-sm text-n-slate-12" />
          </label>
          <label class="text-xs text-n-slate-10 sm:col-span-2">Internal notes
            <textarea v-model="item.internal_notes" rows="2" class="mt-1 w-full rounded-lg border border-n-weak bg-n-alpha-2 p-2 text-sm text-n-slate-12" />
          </label>
        </div>
        <div class="mt-4 flex flex-wrap justify-end gap-2">
          <a :href="whatsappUrl(item)" target="_blank" rel="noreferrer" class="rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-12">Open WhatsApp draft</a>
          <Button label="Save details" sm :is-loading="savingId === item.id" @click="saveDetails(item)" />
        </div>
        <p class="mt-3 text-xs text-n-slate-9">Request #{{ item.id }} · {{ new Date(item.created_at).toLocaleString('en-IN') }}</p>
      </article>
    </section>
  </div>
</template>
